/**
 * AtBatDetailDialog — shared modal for exploring a single at-bat.
 * Rendered by SprayChartExplorer (spray dot click) and PitchAnalysisTab
 * (scatter dot click). Shows zone chart, pitch-by-pitch table, mini spray.
 */
import React from 'react';
import {
  Box, Typography, Chip, Dialog, DialogTitle, DialogContent, IconButton,
  CircularProgress, Grid, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, useTheme, alpha, Tooltip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { getPitchName, PITCH_COLORS } from '../utils/pitchTypes';


const RESULT_LABELS = {
  swstr: 'Swinging K',
  called: 'Called K',
  ball: 'Ball',
  foul: 'Foul',
  inplay: 'In Play',
  hit_by_pitch: 'HBP',
  pitchout: 'Pitch Out',
  blocked: 'Blocked',
};

const RESULT_COLORS = {
  swstr: '#e74c3c',
  called: '#e67e22',
  ball: '#3498db',
  foul: '#f39c12',
  inplay: '#2ecc71',
};

const AB_RESULT_COLORS = {
  single: '#2ecc71', double: '#3498db', triple: '#9b59b6',
  home_run: '#e74c3c', 'home run': '#e74c3c',
  walk: '#3498db', strikeout: '#e74c3c', 'strike out': '#e74c3c',
};
const abResultColor = (r) => {
  const k = (r || '').toLowerCase().replace(/ /g, '_');
  return AB_RESULT_COLORS[k] || AB_RESULT_COLORS[(r || '').toLowerCase()] || '#95a5a6';
};

// ─── Zone Chart ───────────────────────────────────────────────────────────────
const AbZoneChart = ({ pitches, size = 280 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!pitches || pitches.length === 0) return null;

  const szTs = pitches.map(p => p.szT).filter(Boolean);
  const szBs = pitches.map(p => p.szB).filter(Boolean);
  const zoneLeft = -0.833, zoneRight = 0.833;
  const zoneTop  = szTs.length > 0 ? szTs.reduce((a, b) => a + b, 0) / szTs.length : 3.5;
  const zoneBot  = szBs.length > 0 ? szBs.reduce((a, b) => a + b, 0) / szBs.length : 1.5;
  const viewLeft = -2.0, viewRight = 2.0, viewBot = 0, viewTop = 4.5;
  const svgH = size * 1.15;

  const mapX = (x) => ((x - viewLeft)  / (viewRight - viewLeft)) * size;
  const mapY = (y) => ((viewTop - y)   / (viewTop - viewBot))   * svgH;

  return (
    <svg width={size} height={svgH} viewBox={`0 0 ${size} ${svgH}`}>
      <rect width={size} height={svgH} fill={isDark ? '#151a2e' : '#f5f7fa'} rx={8} />

      {/* Strike zone */}
      <rect
        x={mapX(zoneLeft)} y={mapY(zoneTop)}
        width={mapX(zoneRight) - mapX(zoneLeft)}
        height={mapY(zoneBot) - mapY(zoneTop)}
        fill="none"
        stroke={isDark ? '#4a5080' : '#8892a8'} strokeWidth={2}
      />

      {/* 3×3 grid */}
      {[1, 2].map(i => {
        const xF = zoneLeft + (i / 3) * (zoneRight - zoneLeft);
        const yF = zoneBot  + (i / 3) * (zoneTop  - zoneBot);
        return (
          <React.Fragment key={i}>
            <line x1={mapX(xF)} y1={mapY(zoneTop)} x2={mapX(xF)} y2={mapY(zoneBot)}
              stroke={isDark ? '#2a3050' : '#ccd3de'} strokeWidth={0.5} strokeDasharray="3,3" />
            <line x1={mapX(zoneLeft)} y1={mapY(yF)} x2={mapX(zoneRight)} y2={mapY(yF)}
              stroke={isDark ? '#2a3050' : '#ccd3de'} strokeWidth={0.5} strokeDasharray="3,3" />
          </React.Fragment>
        );
      })}

      {/* Home plate */}
      <polygon
        points={`${mapX(-0.7083)},${mapY(0.5)} ${mapX(-0.7083)},${mapY(0.25)} ${mapX(0)},${mapY(0)} ${mapX(0.7083)},${mapY(0.25)} ${mapX(0.7083)},${mapY(0.5)}`}
        fill={isDark ? '#2a3050' : '#e0e4ec'}
        stroke={isDark ? '#3a4060' : '#b0b8c8'} strokeWidth={1}
      />

      {/* Pitches */}
      {pitches.map((p, i) => {
        const x = p.pX ?? 0, y = p.pZ ?? 2.5;
        const color = PITCH_COLORS[p.pitchType] || '#888';
        const isLast = i === pitches.length - 1;
        const r = isLast ? 11 : 9;
        return (
          <Tooltip
            key={i}
            title={`${p.num}. ${p.pitchType} ${p.velocity > 0 ? p.velocity.toFixed(1) + ' mph' : ''} — ${RESULT_LABELS[p.result] || p.result || ''}`}
            arrow
          >
            <g style={{ cursor: 'default' }}>
              <circle
                cx={mapX(x)} cy={mapY(y)} r={r}
                fill={color} opacity={0.88}
                stroke={isLast ? '#fff' : (isDark ? '#0a0e1a' : '#fff')}
                strokeWidth={isLast ? 2.5 : 1.5}
              />
              <text
                x={mapX(x)} y={mapY(y) + 1}
                textAnchor="middle" dominantBaseline="middle"
                fill="#fff" fontSize={r > 9 ? 11 : 9} fontWeight="bold"
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {p.num}
              </text>
            </g>
          </Tooltip>
        );
      })}
    </svg>
  );
};

// ─── Mini Spray ───────────────────────────────────────────────────────────────
const MiniSpray = ({ zone, exitVelo, launchAngle, distance, result }) => {
  const isHit = ['single', 'double', 'triple', 'home_run', 'home run'].some(h => (result || '').toLowerCase().includes(h));
  const isXBH = ['double', 'triple', 'home_run', 'home run'].some(h => (result || '').toLowerCase().includes(h));
  const dotColor = isXBH ? '#e74c3c' : isHit ? '#2ecc71' : '#aaa';
  const col = zone >= 1 && zone <= 9 ? (zone - 1) % 3 : 1;
  const angleRad = ((col - 1) * 35 * Math.PI) / 180;
  const maxR = 52;
  const norm = distance > 0 ? Math.min(distance / 420, 1) : 0.55;
  const r = norm * maxR;
  const cx = 60 + Math.sin(angleRad) * r;
  const cy = 80 - Math.cos(angleRad) * r;
  return (
    <Box sx={{ textAlign: 'center' }}>
      <svg viewBox="0 0 120 90" width={110} height={83} style={{ display: 'block', margin: '0 auto' }}>
        <path d="M60,80 L8,30 A72,72 0 0,1 112,30 Z" fill="rgba(76,175,80,0.08)" stroke="rgba(76,175,80,0.25)" strokeWidth="0.8" />
        <polygon points="60,62 70,55 60,48 50,55" fill="none" stroke="rgba(141,110,99,0.35)" strokeWidth="0.5" />
        <polygon points="57,78 60,82 63,78 62,76 58,76" fill="#fff" stroke="#555" strokeWidth="0.5" />
        <line x1="60" y1="80" x2="34" y2="22" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" strokeDasharray="2,2" />
        <line x1="60" y1="80" x2="86" y2="22" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" strokeDasharray="2,2" />
        <circle cx={cx} cy={cy} r={6} fill={dotColor} opacity={0.9} stroke="rgba(0,0,0,0.25)" strokeWidth={0.8} />
      </svg>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
        {exitVelo ? `${Math.round(exitVelo)} mph` : ''}
        {launchAngle != null ? ` · ${Math.round(launchAngle)}°` : ''}
        {distance ? ` · ${Math.round(distance)} ft` : ''}
      </Typography>
    </Box>
  );
};

// ─── Main Dialog ──────────────────────────────────────────────────────────────
const AtBatDetailDialog = ({ open, onClose, atBat, loading, error }) => {
  const theme = useTheme();

  const lastPitch = atBat?.pitches?.[atBat.pitches.length - 1];
  const hasBattedBall = lastPitch?.exitVelo && lastPitch?.zone;

  // Prep pitches with sequence numbers for the zone chart
  const pitchesWithNum = React.useMemo(() => {
    if (!atBat?.pitches) return [];
    return atBat.pitches.map((p, i) => ({ ...p, num: i + 1 }));
  }, [atBat]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            At-Bat Detail
          </Typography>
          {atBat && (
            <Typography variant="caption" color="text.secondary">
              {atBat.homeAway === 'away' ? '@' : 'vs'} {atBat.opponent}
              {atBat.opponentName ? ` · ${atBat.opponentName}` : ''}
              {' '} · {atBat.gameDate}
              {atBat.inning ? ` · Inning ${atBat.inning}` : ''}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: -0.25 }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
        {/* Loading */}
        {loading && (
          <Box sx={{ py: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Loading at-bat…</Typography>
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>{error}</Typography>
        )}

        {/* Content */}
        {!loading && atBat && (
          <Box>
            {/* Result header chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              <Chip
                label={atBat.result || 'Unknown'}
                size="small"
                sx={{
                  fontWeight: 800, fontSize: '0.8rem',
                  bgcolor: alpha(abResultColor(atBat.result), 0.18),
                  color: abResultColor(atBat.result),
                  border: `1px solid ${alpha(abResultColor(atBat.result), 0.45)}`,
                }}
              />
              <Chip
                label={`${atBat.pitchCount || pitchesWithNum.length} pitch${(atBat.pitchCount || pitchesWithNum.length) !== 1 ? 'es' : ''}`}
                size="small" variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
              {atBat.opponentHand && (
                <Chip
                  label={`vs ${atBat.opponentHand}H`}
                  size="small" variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>

            <Grid container spacing={2}>
              {/* Left — zone chart */}
              <Grid item xs={12} sm={hasBattedBall ? 5 : 6}>
                <Card elevation={0} sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                  <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                    Pitch Locations
                  </Typography>
                  <AbZoneChart pitches={pitchesWithNum} size={240} />

                  {/* Pitch type legend */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, justifyContent: 'center' }}>
                    {[...new Set(pitchesWithNum.map(p => p.pitchType))].map(pt => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS[pt] || '#888' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{getPitchName(pt)}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Mini spray chart for batted balls */}
                  {hasBattedBall && (
                    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', width: '100%' }}>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center', mb: 0.25 }}>
                        Hit Location
                      </Typography>
                      <MiniSpray
                        zone={lastPitch.zone}
                        exitVelo={lastPitch.exitVelo}
                        launchAngle={lastPitch.launchAngle}
                        distance={lastPitch.distance}
                        result={atBat.result}
                      />
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Right — pitch sequence table */}
              <Grid item xs={12} sm={hasBattedBall ? 7 : 6}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                      Pitch Sequence
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }}>Pitch</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }} align="right">Velo</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }} align="right">Spin</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }} align="right">hB</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }} align="right">vB</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, px: 1 }}>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pitchesWithNum.map((p) => {
                          const rc = RESULT_COLORS[p.result] || '#636e72';
                          const isLastP = p.num === pitchesWithNum.length;
                          return (
                            <TableRow
                              key={p.num}
                              sx={{
                                '&:last-child td': { border: 0 },
                                bgcolor: isLastP ? alpha(rc, 0.06) : 'transparent',
                              }}
                            >
                              <TableCell sx={{ py: 0.4, px: 1 }}>
                                <Box sx={{
                                  width: 20, height: 20, borderRadius: '50%',
                                  bgcolor: PITCH_COLORS[p.pitchType] || '#888',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: '#fff', fontSize: '0.72rem', fontWeight: 700,
                                }}>
                                  {p.num}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>
                                {getPitchName(p.pitchType)}
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontFamily: 'monospace' }} align="right">
                                {p.velocity > 0 ? p.velocity.toFixed(1) : '—'}
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }} align="right">
                                {p.spinRate > 0 ? p.spinRate : '—'}
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }} align="right">
                                {p.horizBreak != null ? `${p.horizBreak > 0 ? '+' : ''}${p.horizBreak}"` : '—'}
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }} align="right">
                                {p.vertBreak != null ? `${p.vertBreak > 0 ? '+' : ''}${p.vertBreak}"` : '—'}
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1 }}>
                                <Chip
                                  label={RESULT_LABELS[p.result] || p.result || '—'}
                                  size="small"
                                  sx={{
                                    height: 20, fontSize: '0.71rem', fontWeight: 600,
                                    bgcolor: alpha(rc, 0.14),
                                    color: rc,
                                    border: `1px solid ${alpha(rc, 0.3)}`,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AtBatDetailDialog;
